import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, Flex, Pagination, useMantineColorScheme } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import type { Column, SortColumn } from "react-data-grid";
import DataGrid, { SelectColumn } from "react-data-grid";

import {
  mutatePurchaseOrderItems,
  purchaseOrderItems,
} from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { customFunctions } from "@/config/customFunction";
import { purchaseOrderItemsHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { OrderDetailForm } from "@/features/order/form/detailForm";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { DeliveredForm } from "@/features/purchaseOrderItems/delivered/deliveredForm";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import { useSub } from "@/hooks";
import useQueryString from "@/hooks/useQueryString";
import useUpdateUrlParams from "@/hooks/useUpdateUrlParams";
import { theme } from "@/styles/theme";
import { getUserCodeByUser } from "@/utils/checkData";
import DatetimeUtil from "@/utils/dateTimeUtil";
import timeUtil from "@/utils/timeUtil";
import { ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest } from "@sizlcorp/sizl-api-document/dist/models";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
} from "@sizlcorp/sizl-api-document/dist/models/src/model";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OrderItemsUpdateForm } from "../form/updarteForm";
interface ColorThemeProps {
  isDarkMode: boolean;
}

export const OrderItemsTable = ({ onRowSelect }: any) => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);
  const userCode = getUserCodeByUser();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const [selectedRows, setSelectedRows] = useState((): ReadonlySet<any> => new Set());
  const [selectedRowsInform, setSelectedRowsInform] = useState<
    ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner[]
  >([]);

  const { t } = useTranslation();
  const [formatterPropsData, setFormatterPropsData] = useState<any>([]);
  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });

  const updateUrlParams = useUpdateUrlParams();

  const { openModal, closeModal } = useModal();
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as any | ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("orderItems.orderItems")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
        closeModal(null);
        setSelectedRows(new Set());
        selectedRowsRef.current = new Set();
      },
      onError: () => {
        customAlert(
          `${t("message.deleteFail", { item: `${t("orderItems.orderItems")}` })}`,
          t("common.deleteFail"),
          "red",
        );
      },
    },
  );

  const searchFieldsHeader = purchaseOrderItemsHeader.filter(
    (purchaseOrderItems) => purchaseOrderItems.category === "text",
  );

  const userAuth = customFunctions.ADD_ORDER_USER_AUTH;

  const { data: orderItemsData, refetch } = useQuery(
    purchaseOrderItems.get({
      query: {
        ...query,
        $and: [
          ...(query.$and || []),
          { direction: "OUTBOUND" },
          {
            ...(userAuth
              ? {}
              : {
                  $or: [
                    { creatorUserCode: userCode },
                    { creatorUserCode: "FLOUR" }, // 추후 수정 필요
                    { partnerCode: userCode },
                  ],
                }),
          },
        ],
      },
      search: search.search,
      searchFields: search.searchFields.length
        ? search.searchFields
        : searchFieldsHeader.map((purchaseOrderItems) => purchaseOrderItems.value),
      page: activePage,
      pageSize: Number(search.pageSize),
      sort: sort.length ? sort.join(",") : "-id",
      populate: [
        "itemName",
        "user",
        "quantities",
        "location",
        "partner",
        "purchaseOrderCode",
        "purchaseOrderItemQty",
      ],
    }),
  );

  // 새로운 데이터가 로드될 때, 현재 페이지가 유효하지 않으면 1페이지로 설정
  useEffect(() => {
    if (orderItemsData && activePage > (orderItemsData?.data?.totalPages ?? 0)) {
      setPage(1);
      setQueryString((prev) => ({
        ...prev,
        page: 1,
      }));
    }
  }, [orderItemsData, activePage, setQueryString]);

  // 페이지 Query String으로 공정 이동
  useEffect(() => {
    updateUrlParams(
      {
        page: activePage,
        pageSize: search.pageSize,
        search: search.search,
        searchFields: search.searchFields,
        query: JSON.stringify(query),
        sort: sort,
      },
      queryDefaultValues,
    );

    setSelectedRows(new Set());
    selectedRowsRef.current = new Set();
  }, [activePage, search, query, sort, updateUrlParams]);

  useEffect(() => {
    const selectedRowId = selectedRows.values().next().value;
    if (selectedRowId) {
      const selectedRow = rows.find((row) => row.id === selectedRowId);

      setFormatterPropsData(selectedRow);
    } else {
      setFormatterPropsData([]);
    }
  }, [selectedRows]);

  useEffect(() => {
    // 부모에게 선택된 row를 넘겨주기 위한 함수
    if (formatterPropsData) {
      onRowSelect(selectedRows, formatterPropsData, selectedRowsRef, selectedRowsInform);
    }
  }, [selectedRows, formatterPropsData]);

  const rows: readonly AuthSignupPost201ResponseEquipmentPurchaseOrderItem[] =
    orderItemsData?.data.rows ?? [];
  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());

  const columns: readonly Column<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "partner.name",
      name: t("partner.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps.row.partnerCode} />,
                null,
                `${t("common.detail", { item: `${t("partner.partner")}` })}`,
              )
            }
          >
            {formatterProps.row.partner?.name}
          </DetailLink>
        );
      },
    },
    {
      key: "purchaseOrderId",
      name: `${userCode === "FLOUR" ? t("purchaseOrder.id") : t("order.id")}`,
      sortable: true,
      resizable: true,
      width: 50,
      formatter: (formatterProps) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <OrderDetailForm purchaseOrderId={formatterProps?.row.purchaseOrderId} />,
                null,
                `${userCode === "FLOUR" ? `${t("common.detail", { item: `${t("purchaseOrder.purchaseOrder")}` })}` : `${t("common.detail", { item: `${t("order.order")}` })}`}`,
              )
            }
          >
            {formatterProps.row.purchaseOrderId}
          </DetailLink>
        );
      },
    },
    {
      key: "itemName",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={formatterProps?.row.itemCode} />,
                null,
                `${t("common.detail", { item: `${t("item.item")}` })}`,
              )
            }
          >
            {formatterProps.row.itemName}
          </DetailLink>
        );
      },
    },
    {
      key: "quantity",
      name: t("orderItems.quantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
      formatter: (formatterProps) => {
        return (
          <>
            <div>{formatterProps.row.quantity}</div>
          </>
        );
      },
    },
    {
      key: "sumOfStockPlanQuantity",
      name: t("orderItems.sumOfStockPlanQuantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
    },
    {
      key: "sumOfDeliveredQuantity",
      name: t("orderItems.sumOfDeliveryQuantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
      formatter: (formatterProps) => {
        return (
          <>
            {formatterProps.row.sumOfDeliveredQuantity === "0" ? (
              <div>{formatterProps.row.sumOfDeliveredQuantity}</div>
            ) : (
              <DetailLink
                onClick={() =>
                  openModal(
                    <DeliveredForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    `${t("common.detail", { item: `${t("delivery.deliveryComplete")}` })}`,
                  )
                }
              >
                {formatterProps.row.sumOfDeliveredQuantity}
              </DetailLink>
            )}
          </>
        );
      },
    },
    {
      key: "manager",
      name: t("order.manager"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <UsersDetailForm UserCode={formatterProps.row.userCode} />,
                null,
                `${t("common.detail", { item: `${t("order.manager")}` })}`,
              )
            }
          >
            {formatterProps.row.manager}
          </DetailLink>
        );
      },
    },
    {
      key: "createdAt",
      name: t("orderItems.createdAt"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <div>
            {formatterProps.row.createdAt !== null
              ? DatetimeUtil(formatterProps.row.createdAt as string)
              : ""}
          </div>
        );
      },
    },
    {
      key: "deliveryDeadline",
      name: t("orderItems.deliveryDeadline"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <div>
            {formatterProps.row.deliveryDeadline !== null
              ? timeUtil(formatterProps.row.deliveryDeadline as string)
              : ""}
          </div>
        );
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <OrderItemsUpdateForm formatterProps={formatterProps?.row} />,
                  null,
                  t("common.edit", { item: `${t("order.order")}` }),
                  true,
                );
              }}
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                openModal(
                  <ConfirmForm
                    message={t("message.rowDelete")}
                    yesCallback={() => {
                      deleteMutate({ purchaseOrderItemId: formatterProps?.row?.id as number });
                    }}
                    noCallback={() => {
                      closeModal(null);
                    }}
                  />,
                  null,
                  t("common.delete", { item: `${t("orderItems.orderItems")}` }),
                );
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  useSub("deleteSelectedItems", () => {
    const rows = Array.from(selectedRowsRef.current);
    if (!rows.length) return;
    const deleteRows: number[] = Array.from(rows[rows.length - 1].values());
    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          deleteMutate({ purchaseOrderItemId: deleteRows[deleteRows.length - 1] });
        }}
        noCallback={() => {
          closeModal(null);
        }}
      />,
      null,
      t("common.delete", { item: `${t("orderItems.orderItems")}` }),
    );
  });

  return (
    <OrderItemsTableWrapper>
      <GridWrapper isDarkMode={isDarkMode}>
        <TableWrapper>
          <RetrieveWrapper>
            <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
              <CustomFilter
                filterType={purchaseOrderItemsHeader}
                setQuery={setQuery}
                query={query}
              />
              <CustomSorter sorterType={purchaseOrderItemsHeader} setSort={setSort} sort={sort} />
            </Flex>
            <SearchBox searchType={searchFieldsHeader} setSearch={setSearch} search={search} />
          </RetrieveWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={rows}
            rowHeight={40}
            rowKeyGetter={(row: AuthSignupPost201ResponseEquipmentPurchaseOrderItem) => row.id}
            sortColumns={sortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
              setSelectedRowsInform(selectedRowsInform);
            }}
          />
        </TableWrapper>
        <PaginationWrapper>
          <Pagination
            onChange={(page) => {
              setSelectedRows(new Set());
              setSelectedRowsInform([]);
              setPage(page);
            }}
            value={activePage}
            total={orderItemsData?.data?.totalPages ?? 0}
            size="lg"
            radius="sm"
          />
        </PaginationWrapper>
      </GridWrapper>
    </OrderItemsTableWrapper>
  );
};

const OrderItemsTableWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const GridWrapper = styled.div<ColorThemeProps>`
  & *[role="grid"] {
    height: 440px;
    --rdg-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-header-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-color: ${(props) => (props.isDarkMode ? "black" : "white")};
    --rdg-row-hover-background-color: ${(props) =>
      props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const RetrieveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  justify-content: space-between;
`;
const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;
const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
